import { Check, Loader2, Stethoscope, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useGlobalContext } from '@/store';
import { apiClient } from '@/api/client';
import { ConsultationStepper } from '@/components/ConsultationStepper';
import { SymptomBlockPager } from '@/components/SymptomBlockPager';
import { reorderQuestionsByWeight, reorderUnvisitedQuestions } from '@/utils/symptomReordering';
import { logTemporaryRemedies } from '@/utils/logTemporaryRemedies';
import { getOrCreateE2EEKey } from '@/utils/e2ee';

interface PhysicalSymptomQuestionItem {
  symptom: string;
  question: string;
}

export function PhysicalSymptom() {
  const { patient, setToast, setErrorModal } = useGlobalContext();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<PhysicalSymptomQuestionItem[]>([]);
  const [answers, setAnswers] = useState<Record<string, boolean>>({});
  const [step, setStep] = useState<'init' | 'questions' | 'done'>('init');

  // Pagination and target validation states
  const [blockSize, setBlockSize] = useState<number>(5);
  const [targetCount, setTargetCount] = useState<number>(5);
  const [autoAdvance, setAutoAdvance] = useState<boolean>(true);
  const [currentBlock, setCurrentBlock] = useState<number>(0);
  const [validatedSymptoms, setValidatedSymptoms] = useState<string[]>([]);
  const [symptomRemedyWeights, setSymptomRemedyWeights] = useState<Record<string, Record<string, number>>>({});
  const [symptomRemedyCounts, setSymptomRemedyCounts] = useState<Record<string, number>>({});
  const [symptomRemedyHighRemediesCounts, setSymptomRemedyHighRemediesCounts] = useState<Record<string, { principal_high_remedies: number; total_high_remedies: number; ratio_high_remedies: number }>>({});
  const [linkedByRemedySymptoms, setLinkedByRemedySymptoms] = useState<string[]>([]);
  const [visitedBlocks, setVisitedBlocks] = useState<Set<number>>(new Set([0]));

  const handleBlockChange = useCallback((newBlock: number) => {
    if (!visitedBlocks.has(newBlock)) {
      const currentSelected = Object.entries(answers)
        .filter(([_, val]) => val)
        .map(([key]) => key);
      setQuestions((prevQuestions) =>
        reorderUnvisitedQuestions(
          prevQuestions,
          blockSize,
          visitedBlocks,
          (q) => [q.symptom],
          validatedSymptoms,
          currentSelected,
          symptomRemedyWeights,
          symptomRemedyCounts,
          linkedByRemedySymptoms,
          { symptomRemedyHighRemediesCounts }
        )
      );
      setVisitedBlocks((prev) => new Set([...prev, newBlock]));
    }
    setCurrentBlock(newBlock);
  }, [answers, blockSize, symptomRemedyCounts, symptomRemedyWeights, linkedByRemedySymptoms, validatedSymptoms, visitedBlocks, symptomRemedyHighRemediesCounts]);

  useEffect(() => {
    getOrCreateE2EEKey().catch(() => {});
  }, []);

  useEffect(() => {
    apiClient
      .get('/symptoms/validation-config')
      .then((res) => {
        if (res.data?.block_size) setBlockSize(res.data.block_size);
        if (res.data?.target_affirmative_step3) setTargetCount(res.data.target_affirmative_step3);
        if (typeof res.data?.auto_advance_on_target === 'boolean') {
          setAutoAdvance(res.data.auto_advance_on_target);
        }
      })
      .catch(() => { });
  }, []);

  const startAnalysis = useCallback(async () => {
    if (!patient?.email?.trim()) {
      setToast({ type: 'error', message: 'Patient data is missing.' });
      return;
    }

    setLoading(true);
    try {
      const bodyPartResponse = await apiClient.post('/symptoms/detect-body-part', {
        email: patient.email,
        name: patient.name,
        date_0: new Date().toISOString().slice(0, 10),
        consultation_token: patient.consultationToken,
      });
      const bodyPart = bodyPartResponse.data.body_part || 'General';

      const prospectResponse = await apiClient.post('/symptoms/prospect', {
        email: patient.email,
        name: patient.name,
        date_0: new Date().toISOString().slice(0, 10),
        consultation_token: patient.consultationToken,
        group_symptom: bodyPart,
      });

      const remediesData = prospectResponse.data;

      const response = await apiClient.post('/symptoms/physical-questions', {
        email: patient.email,
        name: patient.name,
        date_0: new Date().toISOString().slice(0, 10),
        consultation_token: patient.consultationToken,
        remedies: remediesData,
      });

      const valSyms = response?.data?.validated_symptoms || [];
      const weightsMap = response?.data?.symptom_remedy_weights || {};
      const countsMap = response?.data?.symptom_remedy_counts || {};
      const linkedSyms = response?.data?.linked_by_remedy_symptoms || [];
      const countsHighMap = response?.data?.symptom_remedy_high_remedies_counts || {};
      setValidatedSymptoms(valSyms);
      setSymptomRemedyWeights(weightsMap);
      setSymptomRemedyCounts(countsMap);
      setLinkedByRemedySymptoms(linkedSyms);
      setSymptomRemedyHighRemediesCounts(countsHighMap);

      const rawQuestions = (response?.data?.items || []) as PhysicalSymptomQuestionItem[];

      // Consultar síntomas existentes del paciente para pre-seleccionar y priorizar preguntas elegidas
      let existingSyms: string[] = [];
      try {
        const symRes = await apiClient.get('/patients/symptoms', {
          params: {
            email: patient.email,
            date_0: new Date().toISOString().slice(0, 10),
            consultation_token: patient.consultationToken,
          },
        });
        existingSyms = (symRes.data?.symptoms || [])
          .filter((s: { step?: number }) => s.step === 3)
          .map((s: { symptom?: string }) => s.symptom?.trim().toLowerCase() || '');
      } catch (_) { }

      const initialAnswers: Record<string, boolean> = {};
      rawQuestions.forEach((q) => {
        const symClean = q.symptom.trim().toLowerCase();
        if (existingSyms.includes(symClean)) {
          initialAnswers[q.symptom] = true;
        }
      });
      setAnswers(initialAnswers);

      const selectedQuestions = rawQuestions.filter((q) => initialAnswers[q.symptom] === true);
      const unselectedQuestions = rawQuestions.filter((q) => !initialAnswers[q.symptom]);

      const initialOrderedUnselected = reorderQuestionsByWeight(
        unselectedQuestions,
        (q) => [q.symptom],
        valSyms,
        Object.keys(initialAnswers),
        weightsMap,
        countsMap,
        linkedSyms,
        { symptomRemedyHighRemediesCounts: countsHighMap }
      );

      const finalOrdered = [...selectedQuestions, ...initialOrderedUnselected];

      setQuestions(finalOrdered);
      setVisitedBlocks(new Set([0]));
      setStep('questions');
    } catch (err: unknown) {
      const errObj = err as { response?: { data?: { detail?: string } } };
      const message = errObj?.response?.data?.detail
        ? String(errObj.response.data.detail)
        : 'Error generating physical symptoms analysis.';
      setErrorModal({ title: 'Error', message });
    } finally {
      setLoading(false);
    }
  }, [patient, setErrorModal, setToast]);

  const savePhysicalSymptoms = useCallback(async (symptomsToSave: string[]) => {
    if (symptomsToSave.length === 0) {
      await logTemporaryRemedies('Physical Symptoms', 3, patient);
      navigate('/mind');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post('/symptoms/save-physical-symptoms', {
        patient: { email: patient.email, name: patient.name, date_0: new Date().toISOString().slice(0, 10), consultation_token: patient.consultationToken },
        symptoms: symptomsToSave,
      });
      await logTemporaryRemedies('Physical Symptoms', 3, patient);
      setToast({ type: 'success', message: 'Additional physical symptoms saved.' });
      navigate('/mind');
    } catch (_err) {
      setToast({ type: 'error', message: 'Error saving answers.' });
    } finally {
      setLoading(false);
    }
  }, [navigate, patient, setToast]);

  const handleSubmit = useCallback(() => {
    const selectedSymptoms = Object.entries(answers)
      .filter(([_, val]) => val === true)
      .map(([key]) => key);
    savePhysicalSymptoms(selectedSymptoms);
  }, [answers, savePhysicalSymptoms]);

  // Habilitar deselección al hacer clic sobre el mismo botón elegido
  const handleAnswer = (symptom: string, value: boolean) => {
    setAnswers((prev) => {
      const currentVal = prev[symptom];
      const updated = { ...prev };
      if (currentVal === value) {
        delete updated[symptom];
      } else {
        updated[symptom] = value;
      }

      const newAffirmativeCount = Object.values(updated).filter(Boolean).length;
      if (autoAdvance && newAffirmativeCount >= targetCount && !loading) {
        setTimeout(() => {
          const selected = Object.entries(updated)
            .filter(([_, val]) => val === true)
            .map(([key]) => key);
          savePhysicalSymptoms(selected);
        }, 100);
      }
      return updated;
    });
  };

  useEffect(() => {
    if (step === 'init' && patient?.name) {
      startAnalysis();
    }
  }, [patient?.name, step, startAnalysis]);

  const affirmativeCount = Object.values(answers).filter(Boolean).length;

  const totalBlocks = Math.ceil(questions.length / blockSize);
  const safeBlock = Math.min(currentBlock, Math.max(0, totalBlocks - 1));
  const currentQuestions = questions.slice(safeBlock * blockSize, (safeBlock + 1) * blockSize);

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      <ConsultationStepper />

      <div className="card bg-base-100 shadow-sm">
        <div className="card-body py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-success text-success-content rounded-full p-3">
                <Stethoscope size={28} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-semibold">Step 3: Physical Symptoms</h2>
                  <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 text-xs px-2.5 py-0.5 rounded-full font-medium border border-emerald-300">
                    🔒 E2EE Encrypted
                  </span>
                </div>
                <p className="text-sm text-base-content/70">
                  Mark the questions that match the patient's physical symptoms. Shared health data is protected via End-to-End Encryption under GDPR & HIPAA standards.
                </p>
              </div>
            </div>
            {step === 'questions' && (
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => navigate('/mind')}>
                Skip step
              </button>
            )}
          </div>
        </div>
      </div>

      {step === 'init' && (
        <div className="card bg-base-100 shadow-sm">
          <div className="card-body items-center text-center py-12">
            <button
              type="button"
              className="btn btn-success btn-lg"
              onClick={startAnalysis}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Analyzing case...
                </>
              ) : (
                'Start physical analysis'
              )}
            </button>
          </div>
        </div>
      )}

      {step === 'questions' && (
        <>
          {questions.length === 0 && (
            <div className="alert alert-info">
              <span>
                No additional physical questions found. You can continue to the next step.
              </span>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {currentQuestions.map((item) => {
              const value = answers[item.symptom];
              return (
                <div key={item.symptom} className={`card bg-base-100 shadow-sm transition-all ${value === true ? 'border-2 border-success/40' : ''}`}>
                  <div className="card-body p-4 gap-2">
                    <p className="text-sm font-medium leading-snug min-h-[3.5rem]">
                      {item.question}
                    </p>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className={`btn btn-sm flex-1 ${value === true ? 'btn-success' : 'btn-outline btn-success'}`}
                        onClick={() => handleAnswer(item.symptom, true)}
                      >
                        <Check size={16} /> Yes
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm flex-1 ${value === false ? 'btn-error' : 'btn-outline btn-error'}`}
                        onClick={() => handleAnswer(item.symptom, false)}
                      >
                        <X size={16} /> No
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {questions.length > 0 && (
            <SymptomBlockPager
              totalItems={questions.length}
              blockSize={blockSize}
              currentBlock={safeBlock}
              onBlockChange={handleBlockChange}
              affirmativeCount={affirmativeCount}
              targetCount={targetCount}
              onAdvancePhase={handleSubmit}
              advanceButtonText="Continue to mental symptoms"
              isSubmitting={loading}
            />
          )}
        </>
      )}
    </div>
  );
}
